// =============================================================================
// BrainScreen.tsx — The Coretel Brain, in the app (Sep 2026).
//
// James: "could sit in admin like the live board and open a new tab browser
// screen with a clean visual of the coretel brain". Full-screen knowledge
// graph of the company vault: every card a node, every [[wikilink]] an edge,
// clustered and coloured by area. Click a node → the card renders in the
// side panel with its links clickable. Search, area filters, pan/zoom.
//
// Data: a JSON snapshot (schema coretel.brain.v1) generated from the Obsidian
// vault and stored in R2 — served through GET /brain/content (manager+).
// "Update snapshot" POSTs a regenerated file. The vault stays the master;
// this screen is a viewer, and the stamp says when the snapshot was cut.
//
// Rendered OUTSIDE AppShell (own route branch in App.tsx, like /display) so
// it owns the whole viewport in its new tab. Gates to manager+ itself.
//
// The area palette is fixed and was validated for colour-vision-deficiency
// separation and contrast on both light/dark surfaces — don't reshuffle it.
// =============================================================================

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Brain, Search, X, Upload, ExternalLink } from "lucide-react";
import { apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Spinner } from "@/components/ui";

// ---------------------------------------------------------------------------
// Types + constants
// ---------------------------------------------------------------------------
interface BrainNode {
  id: string;
  area: string;
  one: string;
  meta: Record<string, string>;
  md: string;
  // sim state (mutated in place — lives in a ref, never in React state)
  x: number; y: number; vx: number; vy: number; deg: number;
}
interface BrainData {
  schema: string;
  generated: string;
  areas: Record<string, [string, string]>;   // key -> [label, colour]
  nodes: BrainNode[];
  edges: [string, string][];
}

const AREA_FALLBACK: [string, string] = ["—", "#888888"];
const disp = (id: string) => id.replace(/^_+/, "");

// ---------------------------------------------------------------------------
// Tiny markdown renderer — headings, bold/italic, inline code, lists, tables,
// blockquotes, hr, links and [[wikilinks]]. The vault is our own trusted
// content (manager+ only), but everything is HTML-escaped first anyway.
// Deliberately no dependency — the cards are simple, disciplined markdown.
// ---------------------------------------------------------------------------
function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function inline(s: string, known: (k: string) => boolean): string {
  s = esc(s);
  s = s.replace(/\[\[([^\]|#]+)(?:#[^\]|]*)?(?:\|([^\]]*))?\]\]/g, (_m, tgt, alias) => {
    const key = String(tgt).trim();
    const label = String(alias || tgt).trim();
    return known(key.toLowerCase())
      ? `<a class="brain-wl" data-id="${key.toLowerCase()}">${label}</a>`
      : label;
  });
  s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[\s(])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  s = s.replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, `<a href="$2" target="_blank" rel="noreferrer">$1</a>`);
  return s;
}
function mdToHtml(md: string, known: (k: string) => boolean): string {
  const lines = md.split("\n");
  const out: string[] = [];
  let i = 0;
  const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
  while (i < lines.length) {
    const l = lines[i];
    if (/^\s*$/.test(l)) { i++; continue; }
    if (/^---\s*$/.test(l) || /^\*\*\*\s*$/.test(l)) { out.push("<hr>"); i++; continue; }
    const h = l.match(/^(#{1,4})\s+(.*)/);
    if (h) { const lv = Math.min(4, h[1].length + 1); out.push(`<h${lv}>${inline(h[2], known)}</h${lv}>`); i++; continue; }
    if (/^>\s?/.test(l)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, "")); i++; }
      out.push(`<blockquote>${inline(buf.join(" "), known)}</blockquote>`);
      continue;
    }
    if (isTableRow(l)) {
      const rows: string[] = [];
      while (i < lines.length && isTableRow(lines[i])) { rows.push(lines[i]); i++; }
      const cells = (r: string) => r.trim().replace(/^\||\|$/g, "").split("|").map(c => c.trim());
      let html = '<div class="brain-tablewrap"><table>';
      let headDone = false;
      for (const r of rows) {
        if (/^\s*\|[\s:|-]+\|\s*$/.test(r)) { headDone = true; continue; }
        const tag = headDone || rows.indexOf(r) > 0 ? "td" : "th";
        html += `<tr>${cells(r).map(c => `<${tag}>${inline(c, known)}</${tag}>`).join("")}</tr>`;
      }
      html += "</table></div>";
      out.push(html);
      continue;
    }
    const li = l.match(/^(\s*)([-*]|\d+\.)\s+(.*)/);
    if (li) {
      const ordered = /\d+\./.test(li[2]);
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)([-*]|\d+\.)\s+(.*)/);
        if (!m) break;
        let item = m[3];
        // hanging continuation lines
        while (i + 1 < lines.length && /^\s{2,}\S/.test(lines[i + 1]) && !lines[i + 1].match(/^(\s*)([-*]|\d+\.)\s/)) {
          i++; item += " " + lines[i].trim();
        }
        items.push(`<li>${inline(item, known)}</li>`);
        i++;
      }
      out.push(ordered ? `<ol>${items.join("")}</ol>` : `<ul>${items.join("")}</ul>`);
      continue;
    }
    // paragraph — gather until blank
    const buf: string[] = [l];
    i++;
    while (i < lines.length && !/^\s*$/.test(lines[i]) && !/^(#{1,4})\s|^>\s?|^---\s*$/.test(lines[i]) && !isTableRow(lines[i]) && !lines[i].match(/^(\s*)([-*]|\d+\.)\s/)) {
      buf.push(lines[i]); i++;
    }
    out.push(`<p>${inline(buf.join(" "), known)}</p>`);
  }
  return out.join("\n");
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------
export default function BrainScreen() {
  const user = useRequireAuth();
  const allowed = !!user && hasRole(user, "manager");

  const [data, setData] = useState<BrainData | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "empty" | "error">("loading");
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hiddenAreas, setHiddenAreas] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const panelBodyRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () => {
    setLoadState("loading");
    try {
      const res = await apiFetch("/brain/content");
      if (res.status === 404) { setLoadState("empty"); return; }
      if (!res.ok) { const e = await res.json().catch(() => ({} as any)); throw new Error(e.detail ?? `load failed (${res.status})`); }
      const d = await res.json() as BrainData;
      d.nodes.forEach(n => { n.x = 0; n.y = 0; n.vx = 0; n.vy = 0; n.deg = 0; });
      setData(d);
      setLoadState("ready");
    } catch (e: any) {
      setLoadErr(e?.message ?? "load failed");
      setLoadState("error");
    }
  }, []);
  useEffect(() => { if (allowed) void loadData(); }, [allowed, loadData]);

  // Derived lookups (stable per data)
  const byId = useMemo(() => {
    const m = new Map<string, BrainNode>();
    data?.nodes.forEach(n => m.set(n.id.toLowerCase(), n));
    return m;
  }, [data]);
  const backlinks = useMemo(() => {
    const m = new Map<string, Set<string>>();
    data?.edges.forEach(([a, b]) => {
      if (!m.has(a)) m.set(a, new Set());
      if (!m.has(b)) m.set(b, new Set());
      m.get(a)!.add(b); m.get(b)!.add(a);
    });
    return m;
  }, [data]);
  const areaKeys = useMemo(() =>
    data ? Object.keys(data.areas).filter(k => data.nodes.some(n => n.area === k)) : [], [data]);
  const areaCounts = useMemo(() => {
    const c: Record<string, number> = {};
    data?.nodes.forEach(n => { c[n.area] = (c[n.area] || 0) + 1; });
    return c;
  }, [data]);

  const selected = selectedId ? byId.get(selectedId) ?? null : null;

  const selectNode = useCallback((id: string, center: boolean) => {
    setSelectedId(id.toLowerCase());
    engineRef.current?.setSelected(id.toLowerCase(), center);
    if (panelBodyRef.current) panelBodyRef.current.scrollTop = 0;
  }, []);

  // -------------------------------------------------------------------------
  // Graph engine — built once per dataset; all mutable state lives here, not
  // in React. Pattern note: repulsion/spring/gravity force sim on canvas,
  // same maths as the artifact prototype this screen was designed from.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!data || !canvasRef.current) return;
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d")!;
    const nodes = data.nodes;
    const idOf = new Map(nodes.map(n => [n.id.toLowerCase(), n]));
    const links = data.edges
      .map(([a, b]) => ({ a: idOf.get(a)!, b: idOf.get(b)! }))
      .filter(l => l.a && l.b);
    nodes.forEach(n => { n.deg = 0; });
    links.forEach(l => { l.a.deg++; l.b.deg++; });

    const keys = Object.keys(data.areas).filter(k => nodes.some(n => n.area === k));
    const angleOf: Record<string, number> = {};
    keys.forEach((k, i) => { angleOf[k] = (i / keys.length) * Math.PI * 2; });
    nodes.forEach(n => {
      const a = angleOf[n.area] ?? 0, r = 240 + Math.random() * 160;
      n.x = Math.cos(a) * r + (Math.random() - 0.5) * 140;
      n.y = Math.sin(a) * r + (Math.random() - 0.5) * 140;
      n.vx = 0; n.vy = 0;
    });

    let alpha = 1;
    let dragNode: BrainNode | null = null;
    let hovered: BrainNode | null = null;
    let selectedN: BrainNode | null = null;
    let hidden = new Set<string>();
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

    function tick() {
      for (let i = 0; i < nodes.length; i++) {
        const ni = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const nj = nodes[j];
          let dx = ni.x - nj.x, dy = ni.y - nj.y;
          let d2 = dx * dx + dy * dy;
          if (d2 < 1) { d2 = 1; dx = Math.random() - 0.5; dy = Math.random() - 0.5; }
          if (d2 > 62500) continue;
          const f = 900 / d2, d = Math.sqrt(d2);
          dx /= d; dy /= d;
          ni.vx += dx * f; ni.vy += dy * f;
          nj.vx -= dx * f; nj.vy -= dy * f;
        }
      }
      links.forEach(l => {
        const dx = l.b.x - l.a.x, dy = l.b.y - l.a.y;
        const d = Math.max(1, Math.hypot(dx, dy));
        const f = (d - 70) * 0.012;
        const fx = dx / d * f, fy = dy / d * f;
        l.a.vx += fx; l.a.vy += fy; l.b.vx -= fx; l.b.vy -= fy;
      });
      nodes.forEach(n => {
        n.vx += -n.x * 0.0035; n.vy += -n.y * 0.0035;
        const a = angleOf[n.area] ?? 0;
        n.vx += (Math.cos(a) * 300 - n.x) * 0.0022;
        n.vy += (Math.sin(a) * 300 - n.y) * 0.0022;
      });
      nodes.forEach(n => {
        if (n === dragNode) { n.vx = 0; n.vy = 0; return; }
        n.x += n.vx * alpha; n.y += n.vy * alpha;
        n.vx *= 0.55; n.vy *= 0.55;
      });
      alpha = Math.max(0.02, alpha * 0.995);
    }
    for (let i = 0; i < (reduced ? 600 : 220); i++) tick();
    if (reduced) alpha = 0.02;

    let W = 0, H = 0, DPR = 1, scale = 1, tx = 0, ty = 0;
    const rOf = (n: BrainNode) => 4 + Math.sqrt(n.deg) * 2.1;
    function resize() {
      DPR = window.devicePixelRatio || 1;
      const r = cv.getBoundingClientRect();
      W = r.width; H = r.height;
      cv.width = W * DPR; cv.height = H * DPR;
      draw();
    }
    function fitView() {
      let minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
      nodes.forEach(n => { minX = Math.min(minX, n.x); maxX = Math.max(maxX, n.x); minY = Math.min(minY, n.y); maxY = Math.max(maxY, n.y); });
      const pad = 70;
      scale = Math.min(1.4, Math.min(W / (maxX - minX + pad * 2), H / (maxY - minY + pad * 2)));
      tx = W / 2 - (minX + maxX) / 2 * scale;
      ty = H / 2 - (minY + maxY) / 2 * scale;
    }
    // App is dark-themed; these read well on the ink surfaces and match the
    // artifact prototype exactly.
    const C = {
      edge: "rgba(180,195,215,.13)", edgeHi: "rgba(232,121,47,.6)",
      ring: "rgba(232,236,241,.25)", accent: "#E8792F",
      label: "#B7C0CC", ink: "#E8ECF1",
    };
    function neighbors(n: BrainNode) { return backlinks.get(n.id) || new Set<string>(); }
    function draw() {
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.translate(tx, ty); ctx.scale(scale, scale);
      const focus = hovered || selectedN;
      const nbr = focus ? neighbors(focus) : null;
      links.forEach(l => {
        if (hidden.has(l.a.area) || hidden.has(l.b.area)) return;
        const hot = focus && (l.a === focus || l.b === focus);
        ctx.strokeStyle = hot ? C.edgeHi : C.edge;
        ctx.lineWidth = (hot ? 1.6 : 1) / scale;
        ctx.beginPath(); ctx.moveTo(l.a.x, l.a.y); ctx.lineTo(l.b.x, l.b.y); ctx.stroke();
      });
      nodes.forEach(n => {
        const isHidden = hidden.has(n.area);
        const dim = isHidden || (focus && n !== focus && !(nbr && nbr.has(n.id)));
        const col = (data!.areas[n.area] ?? AREA_FALLBACK)[1];
        ctx.globalAlpha = isHidden ? 0.08 : dim ? 0.28 : 1;
        ctx.beginPath(); ctx.arc(n.x, n.y, rOf(n), 0, 7);
        ctx.fillStyle = col; ctx.fill();
        ctx.lineWidth = 1 / scale; ctx.strokeStyle = C.ring; ctx.stroke();
        if (n === selectedN) {
          ctx.beginPath(); ctx.arc(n.x, n.y, rOf(n) + 3.5 / scale, 0, 7);
          ctx.lineWidth = 2 / scale; ctx.strokeStyle = C.accent; ctx.stroke();
        }
      });
      ctx.globalAlpha = 1;
      const showAll = scale > 1.35;
      ctx.textAlign = "center";
      nodes.forEach(n => {
        if (hidden.has(n.area)) return;
        const isFocus = n === focus || n === selectedN;
        const isNbr = nbr && nbr.has(n.id);
        if (!showAll && !isFocus && !isNbr) return;
        ctx.font = `${isFocus ? 600 : 400} ${11 / scale}px ui-sans-serif, system-ui, sans-serif`;
        ctx.fillStyle = isFocus ? C.ink : C.label;
        ctx.globalAlpha = isFocus || isNbr ? 1 : 0.8;
        ctx.fillText(disp(n.id), n.x, n.y + rOf(n) + 12 / scale);
      });
      ctx.globalAlpha = 1;
    }
    let raf: number | null = null;
    function loop() {
      if (alpha > 0.021 && !reduced) { tick(); draw(); raf = requestAnimationFrame(loop); }
      else { draw(); raf = null; }
    }
    function reheat(a: number) {
      alpha = Math.max(alpha, a);
      if (!raf && !reduced) raf = requestAnimationFrame(loop);
      else if (reduced) { for (let i = 0; i < 200; i++) tick(); draw(); }
    }
    const toWorld = (px: number, py: number) => [(px - tx) / scale, (py - ty) / scale];
    function nodeAt(px: number, py: number): BrainNode | null {
      const [x, y] = toWorld(px, py);
      let best: BrainNode | null = null;
      const bd = 14 / scale + 4;
      nodes.forEach(n => {
        if (hidden.has(n.area)) return;
        const d = Math.hypot(n.x - x, n.y - y);
        if (d < Math.max(rOf(n) + 4, bd) && (!best || d < Math.hypot(best.x - x, best.y - y))) best = n;
      });
      return best;
    }
    let panning = false, moved = false, lx = 0, ly = 0;
    const onDown = (e: PointerEvent) => {
      cv.setPointerCapture(e.pointerId);
      lx = e.offsetX; ly = e.offsetY; moved = false;
      const n = nodeAt(e.offsetX, e.offsetY);
      if (n) dragNode = n; else panning = true;
    };
    const onMove = (e: PointerEvent) => {
      const dx = e.offsetX - lx, dy = e.offsetY - ly;
      if (dragNode) {
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
        dragNode.x += dx / scale; dragNode.y += dy / scale;
        lx = e.offsetX; ly = e.offsetY;
        reheat(0.12); draw();
      } else if (panning) {
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
        tx += dx; ty += dy; lx = e.offsetX; ly = e.offsetY; draw();
      } else {
        const h = nodeAt(e.offsetX, e.offsetY);
        if (h !== hovered) { hovered = h; cv.style.cursor = h ? "pointer" : "grab"; draw(); }
      }
    };
    const onUp = (e: PointerEvent) => {
      if (!moved) {
        const n = nodeAt(e.offsetX, e.offsetY);
        if (n) selectNode(n.id, false);
      }
      dragNode = null; panning = false; cv.style.cursor = "grab";
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const f = Math.exp(-e.deltaY * 0.0013);
      const ns = Math.min(4, Math.max(0.25, scale * f));
      tx = e.offsetX - (e.offsetX - tx) * (ns / scale);
      ty = e.offsetY - (e.offsetY - ty) * (ns / scale);
      scale = ns; draw();
    };
    cv.addEventListener("pointerdown", onDown);
    cv.addEventListener("pointermove", onMove);
    cv.addEventListener("pointerup", onUp);
    cv.addEventListener("wheel", onWheel, { passive: false });
    const ro = new ResizeObserver(resize);
    ro.observe(cv);
    resize(); fitView(); draw();
    if (!reduced) raf = requestAnimationFrame(loop);

    engineRef.current = {
      setSelected(id: string, center: boolean) {
        selectedN = idOf.get(id) ?? null;
        if (center && selectedN) { tx = W / 2 - selectedN.x * scale; ty = H / 2.4 - selectedN.y * scale; }
        draw();
      },
      setHidden(h: Set<string>) { hidden = new Set(h); draw(); },
      reset() { fitView(); draw(); },
    };
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      cv.removeEventListener("pointerdown", onDown);
      cv.removeEventListener("pointermove", onMove);
      cv.removeEventListener("pointerup", onUp);
      cv.removeEventListener("wheel", onWheel);
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // keep engine in sync with filters; pick a starting card
  useEffect(() => { engineRef.current?.setHidden(hiddenAreas); }, [hiddenAreas, data]);
  useEffect(() => {
    if (data && !selectedId) {
      const start = byId.get("_start here") ?? data.nodes[0];
      if (start) selectNode(start.id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Search results
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!data || q.length < 2) return [];
    const scored: [number, BrainNode][] = [];
    data.nodes.forEach(n => {
      const t = n.id.toLowerCase();
      let s = -1;
      if (t.includes(q)) s = 100 - t.indexOf(q);
      else if ((n.one || "").toLowerCase().includes(q)) s = 40;
      else if (n.md.toLowerCase().includes(q)) s = 10;
      if (s > 0) scored.push([s + n.deg * 0.1, n]);
    });
    scored.sort((a, b) => b[0] - a[0]);
    return scored.slice(0, 10).map(x => x[1]);
  }, [query, data]);

  // Snapshot upload
  const onUploadFile = async (f: File) => {
    setUploading(true); setUploadMsg(null);
    try {
      const text = await f.text();
      const res = await apiFetch("/brain/content", { method: "POST", body: text });
      const out = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error(out.detail ?? out.error ?? `upload failed (${res.status})`);
      setUploadMsg(`Snapshot updated — ${out.cards} cards, ${out.links} links.`);
      setSelectedId(null);
      await loadData();
    } catch (e: any) {
      setUploadMsg(e?.message ?? "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const panelHtml = useMemo(() => {
    if (!selected) return "";
    const body = selected.md
      .replace(/^#\s.+\n/, "")
      .replace(/^>\s*\*\*One line:?\*\*.*\n?/m, "");
    return mdToHtml(body, k => byId.has(k));
  }, [selected, byId]);

  const onPanelClick = (e: React.MouseEvent) => {
    const a = (e.target as HTMLElement).closest("a.brain-wl") as HTMLElement | null;
    if (a?.dataset.id) { e.preventDefault(); if (byId.has(a.dataset.id)) selectNode(a.dataset.id, true); }
  };

  // ------------------------------------------------------------------ render
  if (!user) return null;
  if (!allowed) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center p-6">
        <div className="bg-ink-card border border-line rounded-lg p-6 max-w-sm text-center">
          <Brain size={22} className="mx-auto text-accent mb-2" />
          <div className="text-[15px] font-semibold text-text">Manager access only</div>
          <div className="text-[12.5px] text-text-muted mt-1">The Coretel Brain is available to managers and directors.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-ink text-text flex flex-col overflow-hidden">
      {/* embedded card styles for the rendered markdown */}
      <style>{`
        .brain-body h2{font-size:14px;font-weight:600;margin:18px 0 7px}
        .brain-body h3,.brain-body h4{font-size:13px;font-weight:600;margin:14px 0 5px}
        .brain-body p,.brain-body li{font-size:13px;line-height:1.55;margin:6px 0}
        .brain-body ul,.brain-body ol{padding-left:20px;margin:6px 0}
        .brain-body blockquote{margin:0 0 12px;padding:9px 13px;border-left:3px solid rgb(var(--tw-accent,232 121 47));border-left-color:#E8792F;background:rgba(232,121,47,.10);border-radius:0 8px 8px 0;font-size:13px}
        .brain-body code{font-family:ui-monospace,monospace;font-size:11.5px;background:rgba(140,150,165,.12);border-radius:4px;padding:1px 4px}
        .brain-body .brain-tablewrap{overflow-x:auto;margin:10px 0}
        .brain-body table{border-collapse:collapse;font-size:12px;min-width:100%}
        .brain-body th,.brain-body td{border:1px solid rgba(140,150,165,.25);padding:5px 8px;text-align:left;vertical-align:top}
        .brain-body th{font-weight:600;background:rgba(140,150,165,.08)}
        .brain-body a{color:#E8792F;text-decoration:none}
        .brain-body a.brain-wl{border-bottom:1px dotted #E8792F;cursor:pointer}
        .brain-body a.brain-wl:hover{border-bottom-style:solid}
        .brain-body hr{border:0;border-top:1px solid rgba(140,150,165,.25);margin:14px 0}
      `}</style>

      {/* top bar */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-b border-line bg-ink-card flex-wrap">
        <div className="flex items-baseline gap-2.5 whitespace-nowrap">
          <Brain size={17} className="text-accent self-center" />
          <h1 className="text-[15px] font-bold">The Coretel Brain</h1>
          {data && (
            <span className="text-[10px] font-mono text-text-muted">
              snapshot · {data.generated} · {data.nodes.length} cards · {data.edges.length} links
            </span>
          )}
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-[400px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search the brain — try “crane”, “GST”, “outage”…"
            className="w-full pl-8 pr-7 py-1.5 bg-ink-raised border border-line rounded-md text-[13px] text-text outline-none focus:border-accent"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text" aria-label="Clear search">
              <X size={13} />
            </button>
          )}
          {results.length > 0 && (
            <div className="absolute top-full mt-1.5 left-0 right-0 bg-ink-card border border-line rounded-lg shadow-xl max-h-[320px] overflow-y-auto z-30">
              {results.map(n => {
                const [label, col] = data!.areas[n.area] ?? AREA_FALLBACK;
                return (
                  <button
                    key={n.id}
                    onClick={() => { selectNode(n.id, true); setQuery(""); }}
                    className="block w-full text-left px-3 py-2 border-b border-line/50 last:border-0 hover:bg-accent/10"
                  >
                    <span className="flex items-center gap-2 text-[13px] font-semibold text-text">
                      <span className="w-2 h-2 rounded-full flex-none" style={{ background: col }} />
                      {disp(n.id)}
                    </span>
                    <span className="block text-[11.5px] text-text-muted truncate">{n.one || label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <button
          onClick={() => engineRef.current?.reset()}
          className="px-3 py-1.5 border border-line rounded-md text-[12px] text-text-muted hover:text-text bg-ink-raised"
        >
          Reset view
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-line rounded-md text-[12px] text-text-muted hover:text-text bg-ink-raised disabled:opacity-50"
          title="Upload a regenerated brain snapshot (brain.json)"
        >
          <Upload size={12} /> {uploading ? "Uploading…" : "Update snapshot"}
        </button>
        <input
          ref={fileRef} type="file" accept="application/json,.json" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) void onUploadFile(f); }}
        />
      </div>
      {uploadMsg && (
        <div className="px-4 py-1.5 text-[12px] text-text-muted border-b border-line bg-ink-card">{uploadMsg}</div>
      )}

      {/* main */}
      <div className="flex-1 flex min-h-0">
        <div className="relative flex-1 min-w-0">
          {loadState === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center"><Spinner /></div>
          )}
          {loadState === "empty" && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div className="bg-ink-card border border-line rounded-lg p-6 max-w-md text-center">
                <Brain size={22} className="mx-auto text-accent mb-2" />
                <div className="text-[15px] font-semibold">No brain snapshot yet</div>
                <div className="text-[12.5px] text-text-muted mt-1.5">
                  Upload one with the "Update snapshot" button — the snapshot file (brain.json) is generated from the Coretel Brain vault.
                </div>
              </div>
            </div>
          )}
          {loadState === "error" && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div className="bg-ink-card border border-line rounded-lg p-5 max-w-md text-center text-[12.5px] text-warn">
                Couldn't load the brain: {loadErr}
              </div>
            </div>
          )}
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full cursor-grab" style={{ display: loadState === "ready" ? "block" : "none" }} />
          {loadState === "ready" && data && (
            <>
              <div className="absolute left-3 top-3 flex flex-col gap-1.5 z-[5]">
                {areaKeys.map(k => {
                  const [label, col] = data.areas[k] ?? AREA_FALLBACK;
                  const off = hiddenAreas.has(k);
                  return (
                    <button
                      key={k}
                      onClick={() => {
                        const next = new Set(hiddenAreas);
                        if (off) next.delete(k); else next.add(k);
                        setHiddenAreas(next);
                      }}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-line bg-ink-card text-[11.5px] font-medium ${off ? "opacity-40" : ""}`}
                    >
                      <span className="w-2 h-2 rounded-full flex-none" style={{ background: off ? "#5F6875" : col }} />
                      {label} <span className="text-[10px] font-mono text-text-muted">{areaCounts[k] ?? 0}</span>
                    </button>
                  );
                })}
              </div>
              <div className="absolute left-3 bottom-3 text-[10.5px] font-mono text-text-muted select-none z-[5]">
                drag to pan · scroll to zoom · click a node to read it
              </div>
            </>
          )}
        </div>

        {/* card panel */}
        {loadState === "ready" && selected && (
          <aside className="w-[420px] max-w-[46vw] border-l border-line bg-ink-card flex flex-col min-w-0 max-md:absolute max-md:right-0 max-md:top-0 max-md:bottom-0 max-md:z-20 max-md:w-[min(92vw,400px)] max-md:shadow-2xl">
            <div className="px-5 pt-4 pb-3 border-b border-line">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-text-muted mb-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: (data!.areas[selected.area] ?? AREA_FALLBACK)[1] }} />
                {(data!.areas[selected.area] ?? AREA_FALLBACK)[0]}
              </span>
              <h2 className="text-[18px] font-bold leading-snug">{disp(selected.id)}</h2>
              {selected.one && <p className="text-[13px] text-text-muted mt-1.5 leading-relaxed">{selected.one}</p>}
              {(selected.meta?.kind || selected.meta?.verified) && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {["kind", "verified", "applies_to"].map(k => selected.meta?.[k] ? (
                    <span key={k} className="text-[10px] font-mono text-text-muted bg-ink-raised border border-line/60 rounded px-1.5 py-0.5">
                      {k.replace("_", " ")}: {String(selected.meta[k]).replace(/[[\]]/g, "")}
                    </span>
                  ) : null)}
                </div>
              )}
            </div>
            <div ref={panelBodyRef} className="flex-1 overflow-y-auto px-5 py-4 brain-body" onClick={onPanelClick}>
              <div dangerouslySetInnerHTML={{ __html: panelHtml }} />
              {(() => {
                const bl = [...(backlinks.get(selected.id) ?? [])].sort();
                if (!bl.length) return null;
                return (
                  <div className="mt-6 pt-3 border-t border-line">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-text-muted mb-2">Connected cards ({bl.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {bl.map(id => {
                        const t = byId.get(id);
                        const col = t ? (data!.areas[t.area] ?? AREA_FALLBACK)[1] : "#888";
                        return (
                          <button
                            key={id}
                            onClick={() => selectNode(id, true)}
                            className="inline-flex items-center gap-1.5 text-[12px] text-text border border-line rounded-full px-2.5 py-0.5 hover:border-accent hover:text-accent"
                          >
                            <span className="w-2 h-2 rounded-full" style={{ background: col }} />
                            {disp(t?.id ?? id)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
